import * as React from 'react';
import {ApolloProvider} from 'react-apollo';
// @ts-expect-error
import {DragDropContext as dragDropContext} from 'react-dnd';
// @ts-expect-error
import HTML5Backend from 'react-dnd-html5-backend';
import {Provider} from 'react-redux';
import {Router, Route, Switch, Redirect} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter, Location} from 'react-router-dom';
import {ShepherdJourneyProvider} from 'react-shepherd';

import {ApolloProvider as ReactHooksApolloProvider} from '@apollo/react-hooks';
// @ts-expect-error
import bacon from 'baconjs';

import {formEncodedApi} from 'nutshell-core/api';
import {Routing} from 'nutshell-core/routing';
import {getEntityTypeForPathname} from 'nutshell-core/url/get-entity-type-from-pathname';
import type {User} from 'nutshell-core/users/users-types';

import {nutApolloClient} from '../apollo-client';
import {App as AppModule} from '../app/index';
import {appHistory} from '../history';
// @ts-expect-error
import {Dispatcher} from '../infrastructure/dispatcher';
import {EngagementRootEmbedded} from '../routes/engagement/engagement-root-embedded';
import {store} from '../store';

import {IeDeprecation} from './app-messages/ie-deprecation';
import CustomDragLayer from './dnd/custom-drag-layer';
import {EmailMarketingRootEmbedded} from './email-marketing/email-marketing-root-embedded';
import {NoAccessPage} from './email-marketing/no-access/no-access-page';
import {InboxNoAccessPage} from './inbox/no-access/inbox-no-access-page';
import {NavigationListener} from './navigation-listener';
import {PersistedQueryParams} from './persisted-query-params';
import {RefreshingRoute} from './refreshing-route';
import {RouteContext} from './route-context';
import {SchedulerRootEmbedded} from './scheduler/scheduler-root-embedded';

import '../client_modules/shells/react/components/shared-styles.css';
import 'less/app.less';

type MainAppEmbeddedProps = {
    appState: typeof bacon.Property;
    dispatcher: Dispatcher<any>;
};

type AppProps = ContextRouter;

// TODO (@ianvs): strengthen types
type AppState = {
    initialized: boolean;
    shouldSetTimezone: boolean;
    user: User;
    accounts: any;
    contacts: any;
    leads: any;
    notifications: any[];
    initializedEntities: string[];
};

function MainAppEmbedded({appState, dispatcher}: MainAppEmbeddedProps) {
    const App = dragDropContext(HTML5Backend)(
        class extends React.Component<AppProps, AppState> {
            // @ts-expect-error
            unsubscribeFromState: () => any;
            previousLocation: Location = this.props.location;

            constructor(props: AppProps) {
                super(props);
                this.state = {
                    initialized: false,
                    shouldSetTimezone: NutClientConfig.shouldSetTimezone,
                    // @ts-expect-error
                    user: {},
                    accounts: {},
                    contacts: {},
                    leads: {},
                    notifications: [],
                    // @ts-expect-error
                    initializedEntities: [getEntityTypeForPathname(props.location.pathname)],
                };
                // @ts-expect-error
                this.handleUrlChanges(props);
            }

            // @ts-expect-error
            componentDidUpdate(prevProps, prevState) {
                if (!prevState.initialized && this.state.initialized) {
                    // signal to legacy JS / backbone models that we're mounted (and any
                    // XHR requests are now in flight)
                    $('body').trigger(Nut.events.global.reactWasMountedAndInitialized);
                }
            }

            componentDidMount() {
                // @ts-expect-error
                this.unsubscribeFromState = appState.onValue((state) => {
                    // Not sure why, but this sometimes throws an error.
                    try {
                        this.setState({...state, initialized: true});
                    } catch (e: any) {
                        // @ts-expect-error
                        if (typeof window.trackJs !== 'undefined') {
                            // @ts-expect-error
                            window.trackJs.track(e);
                        }
                    }
                });

                store.dispatch(AppModule.setAppVersion(NutshellFrontendSettings.appVersion));

                document.addEventListener(
                    'Nutshell version checked',
                    this.handleNutshellVersionReceived
                );

                // check to see if we still need to set the timezone for this user
                if (this.state.shouldSetTimezone) {
                    formEncodedApi
                        .post('/dashboard/timezone.json', {
                            timezone: new Intl.DateTimeFormat().resolvedOptions().timeZone,
                        })
                        .then(() => {
                            this.setState({shouldSetTimezone: false});
                        });
                }
            }

            // @ts-expect-error
            UNSAFE_componentWillReceiveProps(nextProps) {
                if (hasLocationChanged(this.props.location, nextProps.location)) {
                    this.handleUrlChanges(nextProps, this.props);
                }
            }

            // Taken from https://reacttraining.com/react-router/web/example/modal-gallery
            // @ts-expect-error
            UNSAFE_componentWillUpdate(nextProps) {
                const {location} = this.props;

                // set previousLocation if props.location is not modal
                if (
                    nextProps.history.action !== 'POP' &&
                    (!location.state || !location.state.modal)
                ) {
                    this.previousLocation = this.props.location;
                }
            }

            componentWillUnmount() {
                this.unsubscribeFromState();
                document.removeEventListener(
                    'Nutshell version checked',
                    this.handleNutshellVersionReceived
                );
            }

            render() {
                const isModal = Boolean(
                    this.props.location.state &&
                        this.props.location.state.modal &&
                        this.previousLocation !== this.props.location
                );

                return (
                    <PersistedQueryParams>
                        {() => {
                            return this.state && this.state.initialized ? (
                                <RouteContext.Provider value={{isEmbedded: true}}>
                                    <Switch
                                        location={
                                            // This keeps the page underneath the modal unchanged
                                            isModal ? this.previousLocation : this.props.location
                                        }
                                    >
                                        <RefreshingRoute
                                            path='/email-marketing'
                                            render={this.renderEmailMarketingRoot}
                                        />
                                        <RefreshingRoute
                                            path='/marketing'
                                            render={this.renderEmailMarketingRoot}
                                        />
                                        <RefreshingRoute
                                            path='/scheduler'
                                            render={this.renderSchedulerRoot}
                                        />
                                        <RefreshingRoute
                                            path='/sales/scheduler'
                                            render={this.renderSchedulerRoot}
                                        />
                                        <Redirect
                                            from='/my-account/calendar'
                                            to='/sales/scheduler/calendar-settings'
                                        />
                                        <RefreshingRoute
                                            path='/inbox'
                                            render={this.renderEngagementRoot}
                                        />
                                    </Switch>
                                    <Route path='/' component={NavigationListener} />
                                    <CustomDragLayer />
                                    <IeDeprecation />
                                </RouteContext.Provider>
                            ) : (
                                <div />
                            );
                        }}
                    </PersistedQueryParams>
                );
            }

            // @ts-expect-error
            handleUrlChanges = (props, oldProps) => {
                // Get the entire url
                const urlState = props.location;
                // Pull apart the queryString (everything after the question mark)
                // and turn each part into separate parts of entire 'app' state
                const deparamedQuery = Routing.deparam(urlState.search.replace(/^\?/, ''));

                dispatcher.push('url', {
                    ...urlState,
                    query: deparamedQuery,
                });

                // If the entity type has changed (going from a client-routed page to a list page),
                // we may need to refetch the detail api response if it was previously loaded
                if (oldProps && props.location.pathname !== oldProps.location.pathname) {
                    const entityType = getEntityTypeForPathname(props.location.pathname);
                    // @ts-expect-error
                    if (this.state.initializedEntities.includes(entityType)) {
                        // @ts-expect-error
                        if (this.state[entityType]) {
                            // @ts-expect-error
                            this.state[entityType].list.refetchPage();
                        }
                    } else {
                        // If this entity type hasn't been loaded before, we don't need to refetch,
                        // but record it as initialized so we can refetch next time.
                        this.setState((prevState) => ({
                            // @ts-expect-error
                            initializedEntities: prevState.initializedEntities.concat(entityType),
                        }));
                    }

                    $('body').trigger(Nut.events.global.pageWasChanged);
                }

                // Update the modal url to display on dismiss
                Modal.updateCurrentPageUrl();
            };

            // @ts-expect-error
            renderEmailMarketingRoot = (props) => {
                // If we have a user, and the user can access Email Marketing,
                // render the root component. Otherwise, show the no access page
                return this.state.user && this.state.user.permissions.canAccessMarketing ? (
                    <EmailMarketingRootEmbedded {...props} user={this.state.user} />
                ) : (
                    <NoAccessPage />
                );
            };

            // @ts-expect-error
            renderSchedulerRoot = (props) => {
                return <SchedulerRootEmbedded {...props} />;
            };

            // @ts-expect-error
            renderEngagementRoot = (props) => {
                const isAgent = this.state.user && this.state.user.permissions.canUseOrTryInbox;

                if (!isAgent) {
                    return <InboxNoAccessPage isEmbedded={true} />;
                }

                return <EngagementRootEmbedded {...props} />;
            };

            handleNutshellVersionReceived = (e: Event) => {
                // @ts-expect-error
                store.dispatch(AppModule.setAppVersion(e.detail));
            };
        }
    );

    return (
        <Provider store={store}>
            <ApolloProvider client={nutApolloClient}>
                <ReactHooksApolloProvider client={nutApolloClient}>
                    <Router history={appHistory}>
                        <ShepherdJourneyProvider>
                            <Route component={App} />
                        </ShepherdJourneyProvider>
                    </Router>
                </ReactHooksApolloProvider>
            </ApolloProvider>
        </Provider>
    );
}

export default MainAppEmbedded;

// @ts-expect-error
function hasLocationChanged(old, current) {
    return (
        old.pathname !== current.pathname ||
        old.search !== current.search ||
        old.hash !== current.hash
    );
}
